import React, { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

import EmbraceSvg from '../assets/icons/EmbraceSvg';
import theme from '../assets/theme';
import { Telemetry } from '../hooks/useTelemetry';
import { CriticalApplicationError } from '../types/errors';

// A client 500 error boundary represents an error caught late enough that the whole page crashes
// and a user can't work around the error
class Client500 extends React.Component<{ children: ReactNode }> {
  state: { hasError: boolean } = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const appVersion = process.env.NEXT_PUBLIC_VERSION || 'unknown';
      // Create a CriticalApplicationError with the caught error as inner error
      const criticalError = new CriticalApplicationError(
        `Application render error: ${error.message}`,
        'client',
        error,
      );

      // Everything worth knowing about the ORIGINAL error travels in Faro's
      // `context`, never on criticalError.stack: Faro's stack-trace parser
      // keeps only `at ...` frame lines and discards the rest, so an
      // "Inner Error:" stack trailer loses its header and silently merges
      // the inner frames into the wrapper's own. `context` is passed through
      // verbatim and the parser never looks at it. See types/errors.ts.
      const errorContext = {
        appVersion,
        innerErrorMessage: error.message,
        innerErrorStack: error.stack ?? '',
        // React's component-tree path to the failing render. Previously only
        // console.error'd below and never actually reported anywhere.
        componentStack: errorInfo.componentStack ?? '',
      };

      if (typeof window == 'undefined') {
        // Required inline, not imported: this is the SSR branch, and Next's
        // client compiler substitutes `typeof window` -> 'object', so the
        // whole branch (and the server-only OTel deps behind it) is
        // dead-code-eliminated from the browser bundle.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { noticeServerError } = require('../utils/serverTelemetry');
        noticeServerError(criticalError, errorContext);
      } else {
        Telemetry()?.noticeError(criticalError, errorContext);
      }

      // Log additional context for debugging
      // eslint-disable-next-line no-console
      console.error('Critical application error:', criticalError);
      // eslint-disable-next-line no-console
      console.error('Component stack:', errorInfo.componentStack);
    } catch (err) {}
  }

  render() {
    if (this.state.hasError) {
      // Fallback error page — "The dog ate this page" (design variation 13). This IS the
      // app's actual 500-error experience: there is no dedicated pages/500.tsx; this
      // boundary wraps the whole app in pages/_app.tsx.
      return (
        <Page>
          <StickerWrap aria-hidden="true">
            <EmbraceSvg />
          </StickerWrap>
          <Headline>The dog ate this page.</Headline>
          <Body>
            Ok, not quite — something broke on our end. Please try again, and if
            you&apos;re still stuck, reach out to us!
          </Body>
          <TryAgainButton
            onClick={() => {
              // The whole React tree just crashed (this boundary wraps <App> itself in
              // pages/_app.tsx), so there's no client router left to push through — a
              // full reload is the only way back to a working app.
              window.location.reload();
            }}
          >
            Try again
          </TryAgainButton>
          <SupportLine>
            Still stuck? Message us in the chat or email{' '}
            <a href="mailto:support@curipod.com">support@curipod.com</a>
          </SupportLine>
        </Page>
      );
    }

    // All is good
    return this.props.children;
  }
}

const float = keyframes`
  0%,
  100% {
    transform: translateY(0) rotate(-4deg);
  }
  50% {
    transform: translateY(-10px) rotate(-1deg);
  }
`;

const ease = 'cubic-bezier(0.645, 0.045, 0.355, 1)';

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px;
  box-sizing: border-box;
  background: ${theme.colors.landingPageBeige};
`;

const StickerWrap = styled.div`
  width: 168px;
  margin-bottom: 24px;
  animation: ${float} 5s ${ease} infinite;
  filter: drop-shadow(0 12px 24px rgba(222, 175, 141, 0.35));

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const Headline = styled.h1`
  margin: 0;
  font-size: 42px;
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};

  @media ${theme.device.mobileL} {
    font-size: 32px;
  }
`;

const Body = styled.p`
  margin: 16px 0 0;
  max-width: 520px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.5;
  color: rgba(40, 47, 51, 0.72);

  @media ${theme.device.mobileL} {
    font-size: 17px;
  }
`;

const TryAgainButton = styled.button`
  margin-top: 32px;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  padding: 15px 40px;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  background: ${theme.colors.orange};
  box-shadow: 0 4px 16px rgba(222, 175, 141, 0.55);
  transition: background 0.4s ${ease}, box-shadow 0.3s ${ease};

  &:hover {
    background: ${theme.colors.orangeDark};
    box-shadow: 0 2px 8px rgba(222, 175, 141, 0.5);
  }
`;

const SupportLine = styled.p`
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.text.darkGrey};

  a {
    color: ${theme.colors.orangeDark};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.black};
      text-decoration: underline;
    }
  }
`;

export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => (
    <Client500>
      <Component {...props} />
    </Client500>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName ||
    Component.name})`;

  return WrappedComponent;
};

export default Client500;
